// Public entry point for the structural-edit applicator.
// Pure function — produces a `ModifiedStatement` (with `plannedEdits[]`) for
// a single `StructuralEdit`. SELECT-side add/remove + modify and DML add/remove
// are all dispatched here.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Structural Edit Applicator — add/remove,
// modify, and Round-Trip Corpus (DML add/remove ops).

import { buildLineIndex, type LineIndex } from '../../sql-grammar/document/lineIndex';
import type { ParseOptions, StatementInfo } from '../../sql-grammar/model';
import type {
  ModifiedStatement,
  ParsedBuffer,
  PlannedByteEdit,
  QueryStructure,
  StructuralEdit,
} from '../types';
import { extractQueryStructure } from '../structure/extractQueryStructure';

import {
  planChangeFromItemTableEdit, planFromItemEdit, planGroupByEdit, planOrderByEdit, planSelectItemEdit,
} from './applyStructuralEdit.lists';
import { planMoveSelectItem, planReorderGroupBy } from './applyStructuralEdit.move';
import { planJoinEdit } from './applyStructuralEdit.joins';
import { planWhereEdit } from './applyStructuralEdit.where';
import { planHavingEdit } from './applyStructuralEdit.having';
import { planModifyEdit } from './applyStructuralEdit.modify';
import {
  planInsertValuesRowEdit, planUpdateAssignmentAddRemove,
} from './applyStructuralEdit.dml';
import {
  planInsertColumnEdit, planModifyInsertValuesCell,
} from './applyStructuralEdit.insertColumns';
import { planReturningEdit } from './applyStructuralEdit.returning';
import { planInsertConflictEdit } from './applyStructuralEdit.conflict';
import { planCteEdit } from './applyStructuralEdit.cte';
import { planNamedWindowEdit } from './applyStructuralEdit.namedWindow';
import { planMergeBranchEdit } from './applyStructuralEdit.merge';
import { planMergeBranchesEdit } from './applyStructuralEdit.mergeBranches';
import { planSetOperatorEdit } from './applyStructuralEdit.setOp';
import { planChangeDmlTargetTableEdit } from './applyStructuralEdit.dmlTarget';
import { planModifyAssignmentTarget, planModifyInsertColumn } from './applyStructuralEdit.renameTargets';
import { planWherePathEdit } from './applyStructuralEdit.wherePath';
import { planWhereGroupEdit } from './applyStructuralEdit.whereGroup';
import { foldConditionPairs } from './foldConditionPairs';

export interface EditCtx {
  readonly source: string;
  readonly options: ParseOptions;
  readonly lineIdx: LineIndex;
  readonly structure: QueryStructure;
  readonly statementInfo: StatementInfo;
}

/**
 * `foldConditionPair` — plan on the structure with such condition pairs merged
 * (`foldConditionPairs`), the same one the caller showed, so indices agree.
 */
export type ApplyStructuralEditOptions = {
  readonly foldConditionPair?: (text: string) => boolean;
};

export function applyStructuralEdit(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
  edit: StructuralEdit,
  applyOptions: ApplyStructuralEditOptions = {},
): ModifiedStatement {
  const source = buffer.parsedDocument.source;
  const extracted = extractQueryStructure(buffer, statementInfo);
  const structure = applyOptions.foldConditionPair
    ? foldConditionPairs(extracted, source, applyOptions.foldConditionPair)
    : extracted;
  const options = buffer.parsedDocument.options;
  const lineIdx = buildLineIndex(source);
  const ctx: EditCtx = { source, options, lineIdx, structure, statementInfo };

  const planned = dispatchEdit(ctx, edit);
  // No local pre-sort: `plannedEdits` is consumed only by `emitSource`, which
  // re-sorts with a stricter total key (startOffset desc, then endOffset desc)
  // before applying right-to-left (SRE invariant 7). A weaker local sort here
  // was redundant — emitSource's order is the single source of truth.

  return {
    original: statementInfo,
    originalStructure: structure,
    applied: [edit],
    plannedEdits: planned,
  };
}

// eslint-disable-next-line complexity
function dispatchEdit(ctx: EditCtx, edit: StructuralEdit): readonly PlannedByteEdit[] {
  switch (edit.op) {
    case 'add-select-item':
    case 'remove-select-item':
      return planSelectItemEdit(ctx, edit);
    case 'move-select-item':
      return planMoveSelectItem(ctx, edit);
    case 'add-from-item':
    case 'remove-from-item':
      return planFromItemEdit(ctx, edit);
    case 'change-from-item-table':
      return planChangeFromItemTableEdit(ctx, edit);
    case 'change-dml-target-table':
      return planChangeDmlTargetTableEdit(ctx, edit);
    case 'add-join':
    case 'remove-join':
      return planJoinEdit(ctx, edit);
    case 'add-where':
    case 'remove-where':
    case 'clear-where':
      return planWhereEdit(ctx, edit);
    case 'add-having':
    case 'remove-having':
    case 'clear-having':
      return planHavingEdit(ctx, edit);
    case 'add-group-by':
    case 'remove-group-by':
      return planGroupByEdit(ctx, edit);
    case 'reorder-group-by':
      return planReorderGroupBy(ctx, edit);
    case 'add-order-by':
    case 'remove-order-by':
      return planOrderByEdit(ctx, edit);
    case 'rename-select-alias':
    case 'modify-join-on':
    case 'modify-join-kind':
    case 'toggle-select-distinct':
    case 'modify-select-window':
    case 'modify-where':
    case 'modify-having':
    case 'modify-limit':
    case 'modify-update-assignment':
      return planModifyEdit(ctx, edit);
    case 'add-insert-values-row':
    case 'remove-insert-values-row':
      return planInsertValuesRowEdit(ctx, edit);
    case 'add-insert-column':
    case 'remove-insert-column':
      return planInsertColumnEdit(ctx, edit);
    case 'modify-insert-values-cell':
      return planModifyInsertValuesCell(ctx, edit);
    case 'modify-insert-column':
      return planModifyInsertColumn(ctx, edit);
    case 'modify-assignment-target':
      return planModifyAssignmentTarget(ctx, edit);
    case 'add-returning-item':
    case 'remove-returning-item':
      return planReturningEdit(ctx, edit);
    case 'modify-insert-conflict-target':
    case 'modify-insert-conflict-action':
    case 'add-insert-conflict-assignment':
    case 'modify-insert-conflict-assignment':
    case 'remove-insert-conflict-assignment':
      return planInsertConflictEdit(ctx, edit);
    case 'add-update-assignment':
    case 'remove-update-assignment':
      return planUpdateAssignmentAddRemove(ctx, edit);
    case 'add-cte':
    case 'remove-cte':
    case 'rename-cte':
      return planCteEdit(ctx, edit);
    case 'rename-named-window':
    case 'remove-named-window':
    case 'add-named-window':
    case 'modify-named-window-body':
      return planNamedWindowEdit(ctx, edit);
    case 'modify-merge-branch-condition':
    case 'modify-merge-update-assignment':
    case 'add-merge-update-assignment':
    case 'remove-merge-update-assignment':
      return planMergeBranchEdit(ctx, edit);
    case 'add-merge-branch':
    case 'remove-merge-branch':
    case 'modify-merge-branch-action':
      return planMergeBranchesEdit(ctx, edit);
    case 'modify-set-operator':
      return planSetOperatorEdit(ctx, edit);
    case 'modify-where-node':
    case 'remove-where-node':
    case 'add-where-node':
      return planWherePathEdit(ctx, edit);
    case 'set-connector':
    case 'toggle-negate':
    case 'unwrap-group':
    case 'add-where-group':
    case 'wrap-group':
    case 'group-leaf':
    case 'move-node':
      return planWhereGroupEdit(ctx, edit);
  }
}
